import React, { useState, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import '../../../../../../../Stylesheets/Dashboard/Tabs/Labor/AddLaborModal.css';
import { createWorker, fetchWorkers, getLaborSummary, refreshLaborProject } from '../../../../../../../Redux/Projects/Labor/laborSlice';
import FirstWorkerSuccessModal from '../Popuops/FirstWorkerSuccessModal';

const AddLaborModal = ({ onClose, currentProjectId }) => {
  const dispatch = useDispatch();
  const [showSuccess, setShowSuccess] = useState(false);

  const { workers } = useSelector(state => state.labor);
  const existingProjectWorkers = workers.filter(w => w.projectId === currentProjectId);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    role: 'Mason',
    address: '',
    wages: '',
  });

  // track touched for nicer UX
  const [touched, setTouched] = useState({});
  const firstInvalidRef = useRef(null);

  const handleChange = (field, value) => {
    // sanitize phone & wages a bit
    if (field === 'phone') value = value.replace(/[^\d]/g, '').slice(0, 15);
    if (field === 'wages') value = value.replace(/[^\d.]/g, '');
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleBlur = (field) => setTouched(prev => ({ ...prev, [field]: true }));

  const handleRoleSelect = (role) => {
    setForm(prev => ({ ...prev, role }));
    setTouched(prev => ({ ...prev, role: true }));
  };

  const errors = useMemo(() => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'First name is required';
    // lastName optional — add if you want it required
    if (!form.phone.trim()) {
      e.phone = 'Phone is required';
    } else if (!/^\d{10}$/.test(form.phone)) {
      e.phone = 'Enter a valid 10-digit phone number';
    }
    if (!form.wages.trim()) {
      e.wages = 'Wages are required';
    } else if (Number.isNaN(Number(form.wages)) || Number(form.wages) <= 0) {
      e.wages = 'Enter a positive number';
    }
    if (!form.role) e.role = 'Role is required';
    return e;
  }, [form]);

  const isValid = Object.keys(errors).length === 0;

  const handleSubmit = async () => {
    if (!isValid) {
      // mark all as touched so errors show
      setTouched({
        firstName: true,
        lastName: true,
        phone: true,
        wages: true,
        role: true,
        address: touched.address, // unchanged
      });

      // focus first invalid input
      const order = ['firstName', 'phone', 'wages', 'role'];
      const firstKey = order.find(k => errors[k]);
      if (firstKey && firstInvalidRef.current?.[firstKey]) {
        firstInvalidRef.current[firstKey].focus();
      }
      return;
    }

    const workerPayload = {
      ...form,
      wages: Number(form.wages),
      projectId: currentProjectId,
    };

    const result = await dispatch(createWorker(workerPayload));

    if (createWorker.fulfilled.match(result)) {
      await dispatch(refreshLaborProject(currentProjectId));

      if (existingProjectWorkers.length === 0) setShowSuccess(true);
      else onClose();
    }
  };

  // refs map for focusing
  firstInvalidRef.current = firstInvalidRef.current || {};
  const setInputRef = (key) => (el) => (firstInvalidRef.current[key] = el);

  const handleAddAnother = () => {
    setShowSuccess(false);
    setForm({
      firstName: '',
      lastName: '',
      phone: '',
      role: 'Mason',
      address: '',
      wages: '',
    });
    setTouched({});
  };

  const handleViewList = () => {
    setShowSuccess(false);
    onClose();
  };

  if (showSuccess) {
    return (
      <FirstWorkerSuccessModal
        onClose={handleViewList}
        onAddAnother={handleAddAnother}
        onViewList={handleViewList}
      />
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal-card modal-lg">
        <div className="modal-header">
          <h2>Add Worker</h2>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          <div className="form-grid">
            <div className={`field ${touched.firstName && errors.firstName ? 'error' : ''}`}>
              <label>First name <span className="req">*</span></label>
              <input
                ref={setInputRef('firstName')}
                type="text"
                value={form.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                onBlur={() => handleBlur('firstName')}
                aria-invalid={!!(touched.firstName && errors.firstName)}
                aria-describedby={touched.firstName && errors.firstName ? 'err-firstName' : undefined}
              />
              {touched.firstName && errors.firstName && (
                <div id="err-firstName" className="error-text">{errors.firstName}</div>
              )}
            </div>

            <div className="field">
              <label>Last name</label>
              <input
                type="text"
                value={form.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
                onBlur={() => handleBlur('lastName')}
              />
            </div>

            <div className={`field ${touched.phone && errors.phone ? 'error' : ''}`}>
              <label>Phone Number <span className="req">*</span></label>
              <input
                ref={setInputRef('phone')}
                type="tel"
                inputMode="numeric"
                value={form.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                onBlur={() => handleBlur('phone')}
                placeholder="10-digit number"
                aria-invalid={!!(touched.phone && errors.phone)}
                aria-describedby={touched.phone && errors.phone ? 'err-phone' : undefined}
              />
              {touched.phone && errors.phone && (
                <div id="err-phone" className="error-text">{errors.phone}</div>
              )}
            </div>

            <div className={`field ${touched.wages && errors.wages ? 'error' : ''}`}>
              <label>Wages / day <span className="req">*</span></label>
              <input
                ref={setInputRef('wages')}
                type="text"
                inputMode="decimal"
                value={form.wages}
                onChange={(e) => handleChange('wages', e.target.value)}
                onBlur={() => handleBlur('wages')}
                placeholder="e.g., 800"
                aria-invalid={!!(touched.wages && errors.wages)}
                aria-describedby={touched.wages && errors.wages ? 'err-wages' : undefined}
              />
              {touched.wages && errors.wages && (
                <div id="err-wages" className="error-text">{errors.wages}</div>
              )}
            </div>

            <div className={`field field--full ${touched.role && errors.role ? 'error' : ''}`}>
              <label>Role <span className="req">*</span></label>
              <div className="role-btns" role="group" aria-label="Select role">
                {[
                  'Mason', 'Electrician', 'Plumber', 'Carpenter', 'Painter', 'Welder',
                  'Site Supervisor', 'Helper', 'Steel Fixer', 'Bar Bender',
                  'Machine Operator', 'Safety Officer', 'Others'
                ].map(role => (
                  <button
                    key={role}
                    type="button"
                    className={`role-btn ${form.role === role ? 'selected' : ''}`}
                    onClick={() => handleRoleSelect(role)}
                    onBlur={() => handleBlur('role')}
                    aria-pressed={form.role === role}
                  >
                    {role}
                  </button>
                ))}
              </div>
              {touched.role && errors.role && (
                <div className="error-text">{errors.role}</div>
              )}
            </div>

            <div className="field field--full">
              <label>Address (Optional)</label>
              <input
                type="text"
                value={form.address}
                onChange={(e) => handleChange('address', e.target.value)}
                onBlur={() => handleBlur('address')}
              />
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button
            className="add-btn"
            onClick={handleSubmit}
            disabled={!isValid}
            aria-disabled={!isValid}
            title={!isValid ? 'Please fill required fields' : 'Add Worker'}
          >
            Add Worker
          </button>
          <button className="cancel-btn-labor" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

export default AddLaborModal;
